import {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  FieldErrors,
} from "react-hook-form";
import { Container } from "lucide-react";
import { FormField, Input, Select, Textarea } from "../UI/FormField";
import { SectionCard } from "../UI/SectionCard";
import type { ContainerOrderFormData } from "../../types/containerOrders";
import { CONTAINER_TYPE_OPTIONS } from "../../constants/containerOrderConstants";
import { IMO_CLASS_OPTIONS } from "../../constants/shipmentFormConstants";
import { useTranslation } from "react-i18next";

interface ContainerDefaultsSectionProps {
  register: UseFormRegister<ContainerOrderFormData>;
  setValue: UseFormSetValue<ContainerOrderFormData>;
  watch: UseFormWatch<ContainerOrderFormData>;
  errors: FieldErrors<ContainerOrderFormData>;
  readonly: boolean;
  getErrorMessage: (error: any) => string | undefined;
}

/**
 * Section 2 — container defaults, shared by every generated shipment.
 *
 * Matches the mockup: container type, shipping line, vessel / voyage, ISO code,
 * default tare, default max payload and the three handling flags.
 */
/**
 * Which detail field each handling flag owns. Unchecking a flag clears its
 * detail, so the order never stores an IMO class for cargo that is no longer
 * flagged as ADR.
 */
const HANDLING_DETAILS = {
  temperatureSensitive: "handling.temperatureRange",
  adr: "handling.imoClass",
  fragile: "handling.specifications",
} as const;

export function ContainerDefaultsSection({
  register,
  setValue,
  watch,
  errors,
  readonly,
  getErrorMessage,
}: ContainerDefaultsSectionProps) {
  const { t } = useTranslation(["containerOrders", "shipments"]);

  const temperatureSensitive = watch("handling.temperatureSensitive");
  const adr = watch("handling.adr");
  const fragile = watch("handling.fragile");

  // Uncontrolled — RHF seeds the initial state from defaultValues and owns the
  // change handler, so no `checked` prop (which would fight it). The extra
  // onChange clears the flag's detail on the way *down* only: unchecking hides
  // the field, and hiding a field that still holds a value would silently keep
  // submitting it. Re-checking therefore starts empty, which is the honest
  // state — nobody has re-entered a temperature range yet.
  const checkbox = (
    name: keyof typeof HANDLING_DETAILS,
    label: string,
  ) => (
    <label className="flex cursor-pointer items-center gap-1.5 text-[12px] text-secondary-600">
      <input
        type="checkbox"
        {...register(`handling.${name}` as const, {
          onChange: (event) => {
            if (!event.target.checked) {
              setValue(HANDLING_DETAILS[name], "", {
                shouldDirty: true,
                shouldValidate: true,
              });
            }
          },
        })}
        disabled={readonly}
        className="h-3.5 w-3.5 shrink-0 rounded border-secondary-300 text-primary accent-primary"
      />
      {label}
    </label>
  );

  return (
    <SectionCard icon={Container} title={t("sections.containerDefaults")}>
      <div className="space-y-1">
        <FormField
          layout="inline"
          size="sm"
          label={t("labels.containerType")}
          required
          error={getErrorMessage(errors.containerType)}
        >
          <Select
            {...register("containerType")}
            options={CONTAINER_TYPE_OPTIONS}
            disabled={readonly}
            size="sm"
            placeholder={t("placeholders.selectContainerType")}
            error={!!errors.containerType}
          />
        </FormField>

        <FormField
          layout="inline"
          size="sm"
          label={t("labels.shippingLine")}
          error={getErrorMessage(errors.shippingLine)}
        >
          <Input
            {...register("shippingLine")}
            size="sm"
            readOnly={readonly}
            placeholder={t("placeholders.shippingLine")}
          />
        </FormField>

        {/* Vessel + voyage — one row, two inputs, as in the mockup. */}
        <FormField
          layout="inline"
          size="sm"
          label={t("labels.vesselVoyage")}
          error={
            getErrorMessage(errors.vesselName) ??
            getErrorMessage(errors.voyageNumber)
          }
        >
          <div className="grid grid-cols-1 gap-2 @2xs/control:grid-cols-2">
            <Input
              {...register("vesselName")}
              size="sm"
              readOnly={readonly}
              placeholder={t("placeholders.vessel")}
            />
            <Input
              {...register("voyageNumber")}
              size="sm"
              readOnly={readonly}
              placeholder={t("placeholders.voyage")}
            />
          </div>
        </FormField>

        {/* The batch's Bill of Lading. A default: it cascades onto every
            container generated from this order, and each one can then differ,
            because a booking is routinely split across several BLs. */}
        <FormField
          layout="inline"
          size="sm"
          label={t("labels.blNumber")}
          hint={t("hints.blNumber")}
          error={getErrorMessage(errors.blNumber)}
        >
          <Input
            {...register("blNumber")}
            size="sm"
            readOnly={readonly}
            placeholder={t("placeholders.blNumber")}
          />
        </FormField>

        <FormField
          layout="inline"
          size="sm"
          label={t("labels.isoCode")}
          error={getErrorMessage(errors.isoCode)}
          hint={t("hints.isoCode")}
        >
          <Input
            {...register("isoCode")}
            size="sm"
            readOnly={readonly}
            className="font-mono"
            placeholder={t("placeholders.isoCode")}
          />
        </FormField>

        <FormField
          layout="inline"
          size="sm"
          label={t("labels.defaultTare")}
          error={getErrorMessage(errors.defaultTareKg)}
        >
          {/* `deps` re-runs the payload's check whenever the tare moves: the
              rule that binds them is reported on the payload field, so without
              this, correcting the tare leaves the complaint sitting under a box
              that is no longer at fault. */}
          <Input
            {...register("defaultTareKg", {
              valueAsNumber: true,
              deps: ["defaultMaxPayloadKg"],
            })}
            type="number"
            min={0}
            step="1"
            size="sm"
            readOnly={readonly}
            placeholder="kg"
            error={!!errors.defaultTareKg}
          />
        </FormField>

        <FormField
          layout="inline"
          size="sm"
          label={t("labels.defaultMaxPayload")}
          error={getErrorMessage(errors.defaultMaxPayloadKg)}
        >
          <Input
            {...register("defaultMaxPayloadKg", { valueAsNumber: true })}
            type="number"
            min={0}
            step="1"
            size="sm"
            readOnly={readonly}
            placeholder="kg"
            error={!!errors.defaultMaxPayloadKg}
          />
        </FormField>


        <FormField layout="inline" size="sm" label={t("labels.handling")}>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 py-1">
            {checkbox("temperatureSensitive", t("handling.temperature"))}
            {checkbox("adr", t("handling.adr"))}
            {checkbox("fragile", t("handling.fragile"))}
          </div>
        </FormField>

        {/* Each flag's detail appears only while the flag is on — the same
            pairing the shipment form uses, so an order and the shipments it
            generates ask for cargo handling in exactly the same terms. */}
        {temperatureSensitive && (
          <FormField
            layout="inline"
            size="sm"
            label={t("labels.tempRange")}
            error={getErrorMessage(errors.handling?.temperatureRange)}
          >
            <Input
              {...register("handling.temperatureRange")}
              size="sm"
              readOnly={readonly}
              placeholder={t("placeholders.tempRange")}
              error={!!errors.handling?.temperatureRange}
            />
          </FormField>
        )}

        {adr && (
          <FormField
            layout="inline"
            size="sm"
            label={t("labels.imoClass")}
            error={getErrorMessage(errors.handling?.imoClass)}
          >
            <Select
              {...register("handling.imoClass")}
              options={IMO_CLASS_OPTIONS}
              disabled={readonly}
              size="sm"
              placeholder={t("placeholders.selectImoClass")}
              error={!!errors.handling?.imoClass}
            />
          </FormField>
        )}

        {fragile && (
          <FormField
            layout="inline"
            size="sm"
            label={t("labels.specifications")}
            error={getErrorMessage(errors.handling?.specifications)}
          >
            <Textarea
              {...register("handling.specifications")}
              size="sm"
              rows={2}
              readOnly={readonly}
              placeholder={t("placeholders.specifications")}
              error={!!errors.handling?.specifications}
            />
          </FormField>
        )}
      </div>
    </SectionCard>
  );
}
